// Two sets of hooks here:
//   - Customer-facing: useUnreadChangelog (bell-dot + popover trigger),
//     useMarkChangelogSeen, usePublishedChangelog (for the /whats-new
//     viewer in the profile dropdown if ever added).
//   - Admin: useAdminChangelog, useCreateChangelog, usePatchChangelog,
//     useDeleteChangelog.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";

export interface ChangelogEntry {
  id: string;
  version: string;
  title: string;
  body: string;
  audience: "all" | "admin";
  major: boolean;
  publishedAt: string | null;
  createdAt?: string;
  createdById?: string | null;
  updatedAt?: string;
}

export interface UnreadChangelog {
  count: number;
  hasMajor: boolean;
  latest: Array<
    Pick<ChangelogEntry, "id" | "version" | "title" | "body" | "major" | "publishedAt">
  >;
}

export function useUnreadChangelog(enabled: boolean) {
  return useQuery<UnreadChangelog>({
    queryKey: ["changelog", "unread"],
    queryFn: async () => {
      try {
        return await api<UnreadChangelog>("/api/changelog/unread");
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          // Treat unauthenticated as "nothing unread" so the bell-dot
          // stays hidden on the login screen race.
          return { count: 0, hasMajor: false, latest: [] };
        }
        throw err;
      }
    },
    enabled,
    refetchOnWindowFocus: true,
    staleTime: 60_000,
    retry: false,
  });
}

export function useMarkChangelogSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: true }>("/api/changelog/mark-seen", { method: "POST" }),
    onSuccess: () => {
      // Optimistic: the unread count should be 0 after this.
      qc.setQueryData<UnreadChangelog>(["changelog", "unread"], {
        count: 0,
        hasMajor: false,
        latest: [],
      });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function usePublishedChangelog(params: { page?: number; pageSize?: number }) {
  return useQuery<{
    rows: Array<
      Pick<
        ChangelogEntry,
        "id" | "version" | "title" | "body" | "major" | "audience" | "publishedAt"
      >
    >;
    total: number;
    page: number;
    pageSize: number;
  }>({
    queryKey: ["changelog", "published", params],
    queryFn: () =>
      api(
        `/api/changelog/published?page=${params.page ?? 1}&pageSize=${params.pageSize ?? 20}`,
      ),
    staleTime: 60_000,
  });
}

// ─── Admin ──────────────────────────────────────────────────────────────

export function useAdminChangelog(params: { page?: number; pageSize?: number; includeDrafts?: boolean }) {
  return useQuery<{ rows: ChangelogEntry[]; total: number; page: number; pageSize: number }>({
    queryKey: ["sysadmin", "changelog", params],
    queryFn: () => {
      const u = new URLSearchParams();
      if (params.page) u.set("page", String(params.page));
      if (params.pageSize) u.set("pageSize", String(params.pageSize));
      if (params.includeDrafts !== undefined)
        u.set("includeDrafts", String(params.includeDrafts));
      return api(`/api/sysadmin/changelog?${u.toString()}`);
    },
    placeholderData: (prev) => prev,
  });
}

export interface ChangelogCreateInput {
  version: string;
  title: string;
  body: string;
  audience: "all" | "admin";
  major: boolean;
  publish: boolean;
}

export function useCreateChangelog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ChangelogCreateInput) =>
      api<ChangelogEntry>("/api/sysadmin/changelog", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sysadmin", "changelog"] });
      qc.invalidateQueries({ queryKey: ["changelog"] });
    },
  });
}

export interface ChangelogPatchInput {
  id: string;
  patch: Partial<{
    version: string;
    title: string;
    body: string;
    audience: "all" | "admin";
    major: boolean;
    publishedAt: null | "now";
  }>;
}

export function usePatchChangelog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: ChangelogPatchInput) =>
      api<ChangelogEntry>(`/api/sysadmin/changelog/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sysadmin", "changelog"] });
      qc.invalidateQueries({ queryKey: ["changelog"] });
    },
  });
}

export function useDeleteChangelog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api(`/api/sysadmin/changelog/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sysadmin", "changelog"] });
      qc.invalidateQueries({ queryKey: ["changelog"] });
    },
  });
}
